function sum(items) {
  return items.reduce((total, item) => total + Math.max(0, Number(item.value) || 0), 0);
}

export function DonutChartCard({ title, subtitle, items }) {
  const total = sum(items);
  let offset = 0;
  const circumference = 2 * Math.PI * 44;

  return (
    <article className="scorecard-chart-card">
      <div className="scorecard-chart-card__header">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <strong>{total}</strong>
      </div>
      <div className="scorecard-donut-layout">
        <svg className="scorecard-donut" viewBox="0 0 120 120" role="img" aria-label={`${title}: ${total} total`}>
          <circle className="scorecard-donut__track" cx="60" cy="60" r="44" />
          {total > 0 && items.map((item, index) => {
            const value = Math.max(0, Number(item.value) || 0);
            const length = (value / total) * circumference;
            const segment = (
              <circle
                key={item.label}
                className={`scorecard-donut__segment scorecard-tone-${item.tone || index + 1}`}
                cx="60"
                cy="60"
                r="44"
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return segment;
          })}
          <text className="scorecard-donut__number" x="60" y="58" textAnchor="middle">{total}</text>
          <text className="scorecard-donut__caption" x="60" y="75" textAnchor="middle">Total</text>
        </svg>
        <div className="scorecard-legend">
          {items.map((item, index) => {
            const value = Number(item.value) || 0;
            const pct = total ? Math.round((value / total) * 100) : 0;
            return (
              <div className="scorecard-legend__row" key={item.label}>
                <span className={`scorecard-legend__dot scorecard-tone-bg-${item.tone || index + 1}`} />
                <span>{item.label}</span>
                <strong>{value}</strong>
                <em>{pct}%</em>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}

export function BarChartCard({ title, subtitle, items, emptyLabel = 'No data for this period.' }) {
  const max = Math.max(0, ...items.map((item) => Number(item.value) || 0));
  return (
    <article className="scorecard-chart-card">
      <div className="scorecard-chart-card__header">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      {items.length ? (
        <div className="scorecard-bars">
          {items.map((item, index) => {
            const value = Number(item.value) || 0;
            const width = max > 0 ? Math.max(3, (value / max) * 100) : 0;
            return (
              <div className="scorecard-bar-row" key={`${item.label}-${index}`}>
                <div className="scorecard-bar-row__top">
                  <span>{item.label}</span>
                  <strong>{value}</strong>
                </div>
                <div className="scorecard-bar-track">
                  <div className={`scorecard-bar-fill scorecard-tone-bg-${item.tone || ((index % 3) + 1)}`} style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : <div className="scorecard-chart-empty">{emptyLabel}</div>}
    </article>
  );
}

export function TatChartCard({ items, averageTat }) {
  return (
    <article className="scorecard-chart-card">
      <div className="scorecard-chart-card__header">
        <div>
          <h2>TAT Performance</h2>
          <p>Uses the shared KPI service's current turnaround-time rules.</p>
        </div>
        <strong>{averageTat === null || averageTat === undefined ? '—' : `${averageTat.toFixed(2)}h`}</strong>
      </div>
      {items.length ? (
        <div className="scorecard-tat-list">
          {items.map((item) => {
            const total = item.onTime + item.late;
            const pct = total ? (item.onTime / total) * 100 : 0;
            return (
              <div className="scorecard-tat-row" key={item.label}>
                <div className="scorecard-tat-row__top">
                  <strong>{item.label}</strong>
                  <span>{item.averageTat === null ? '—' : `${Number(item.averageTat).toFixed(2)}h avg`}</span>
                </div>
                <div className="scorecard-bar-track">
                  <div className="scorecard-bar-fill scorecard-tone-bg-3" style={{ width: `${pct}%` }} />
                </div>
                <div className="scorecard-tat-row__meta">{item.onTime} on time · {item.late} late</div>
              </div>
            );
          })}
        </div>
      ) : <div className="scorecard-chart-empty">Detailed TAT by role is not available for this period.</div>}
    </article>
  );
}
